/**
 * The tile layer for the satellite preview, and the guard that keeps it from costing money.
 *
 * WHY `bounds` MATTERS. Every tile the browser asks for is one Google Earth Engine render
 * behind our proxy. A tile layer without `bounds` covers the world, so Leaflet requests
 * every tile in the viewport at the current zoom — including all the tiles that only show
 * the neighbours' land, which is most of them. The preview response already carries the
 * field's own bbox (`rois.full_roi.bounds`, backend/app/api/tiles.py), and a layer that
 * declares it makes Leaflet skip every tile that does not intersect the field.
 *
 * VERIFIED IN THE LIBRARY, NOT ASSUMED. leaflet 1.9.4, `GridLayer._isValidTile`
 * (`dist/leaflet-src.js:11822`): `if (!this.options.bounds) { return true; }` and otherwise
 * `toLatLngBounds(this.options.bounds).overlaps(tileBounds)`. It is called from `_update`
 * (line 11786) with a `continue` BEFORE `queue.push(coords)`, so a tile outside the box is
 * never created, no `<img>` appears and no request goes out. `preview.test.ts` drives a live
 * `L.TileLayer` on a live `L.Map` and counts the tiles, because an option lost during a
 * rebuild of the layer looks exactly like an option that works.
 *
 * NOTE: `overlaps`, not `contains` — edge tiles are still requested. MapLibre behaved the
 * same way; this is not a regression.
 *
 * THE MAIN PRODUCT DOES NOT HAVE THIS. Its `SentinelOverlay`
 * (`frontend/src/components/ui/DrawFieldMap.tsx:46`) builds `L.tileLayer(url, …)` with no
 * `bounds` at all; its only guard is zoom ≥ 14. So this is new code, not a port, and "their
 * map works" says nothing about it.
 */

import * as L from "leaflet";

import { PANES } from "./panes";
import { toLeafletBounds } from "./latlng";

export const PREVIEW_ATTRIBUTION = "Sentinel-2 / Copernicus via Google Earth Engine";

export type PreviewBounds = [number, number, number, number];

/** [west, south, east, north], or null if the response carried no usable bbox. */
export const asPreviewBounds = (bounds: number[] | null | undefined): PreviewBounds | null => {
  if (!bounds || bounds.length !== 4) return null;
  if (!bounds.every((value) => typeof value === "number" && Number.isFinite(value))) return null;
  const [west, south, east, north] = bounds as PreviewBounds;
  // A degenerate or inverted box would hide the layer completely; better to show the whole
  // viewport than nothing at all.
  if (west >= east || south >= north) return null;
  return [west, south, east, north];
};

/**
 * The tile-saving options, ported from the main product's `SentinelOverlay` — where they are
 * written as layer options and are in fact cost guards, which is why they are named as such:
 *
 *   `updateWhenIdle`     no requests at all while the map is being dragged;
 *   `updateWhenZooming`  none mid-zoom-animation either;
 *   `updateInterval`     at most one update every 500 ms;
 *   `keepBuffer: 1`      one ring of off-screen tiles instead of Leaflet's two;
 *   `maxNativeZoom: 18`  past 18 the last real level is stretched, not re-requested.
 */
export const previewLayerOptions = (bounds: PreviewBounds | null): L.TileLayerOptions => ({
  pane: PANES.preview,
  maxZoom: 22,
  maxNativeZoom: 18,
  keepBuffer: 1,
  updateWhenIdle: true,
  updateWhenZooming: false,
  updateInterval: 500,
  attribution: PREVIEW_ATTRIBUTION,
  // Faded in by the cross-fade in MapCanvas: a hard swap on every date change flashes white.
  opacity: 0,
  ...(bounds ? { bounds: toLeafletBounds(bounds) } : {}),
});

export const previewLayer = (tileUrl: string, bounds: PreviewBounds | null): L.TileLayer =>
  L.tileLayer(tileUrl, previewLayerOptions(bounds));
